import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import PagesManager from "../components/admin/PagesManager";
import BlogManager from "../components/admin/BlogManager";
import SettingsManager from "../components/admin/SettingsManager";
import AnalyticsManager from "../components/admin/AnalyticsManager";
import { logo } from "../mock";
import { inputCls, labelCls } from "../components/admin/SeoFields";
import { FileText, Newspaper, Settings, LogOut, Lock, Loader2, ExternalLink, BarChart3 } from "lucide-react";

const LoginScreen = () => {
  const { login } = useAuth();
  const { toast } = useToast();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(u, p);
      toast({ title: "Welcome back!" });
    } catch (err) {
      toast({ title: "Login failed", description: err.response?.data?.detail || "Invalid credentials", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 grid-bg">
      <div className="glow-blob w-80 h-80 bg-[#00ff87] top-10 left-10" />
      <form onSubmit={submit} className="relative z-10 w-full max-w-sm card-neon rounded-2xl p-8">
        <img src={logo} alt="DaddyAnna" className="h-12 mx-auto mb-6" />
        <div className="flex items-center justify-center gap-2 text-[#00ff87] mb-6">
          <Lock size={18} /> <span className="font-display font-semibold">Admin Login</span>
        </div>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Username</label>
            <input className={inputCls} value={u} onChange={(e) => setU(e.target.value)} placeholder="admin" />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input type="password" className={inputCls} value={p} onChange={(e) => setP(e.target.value)} placeholder="••••••••" />
          </div>
          <button disabled={busy} className="btn-neon w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />} Sign In
          </button>
        </div>
      </form>
    </div>
  );
};

const Admin = () => {
  const { isAuthed, loading, logout, username } = useAuth();
  const [tab, setTab] = useState("analytics");

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-[#00ff87]" size={40} /></div>;
  if (!isAuthed) return (<><Toaster /><LoginScreen /></>);

  const tabs = [
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "pages", label: "Pages", icon: FileText },
    { id: "blog", label: "Blog", icon: Newspaper },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#07070d]">
      <Toaster />
      <header className="border-b border-white/10 bg-[#0a0a13] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="DaddyAnna" className="h-9" />
            <span className="text-gray-500 text-sm hidden sm:inline">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#00e5ff]"><ExternalLink size={15} /> View Site</a>
            <span className="text-sm text-gray-400">Hi, {username}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-red-500"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-[200px_1fr] gap-8">
        <nav className="flex md:flex-col gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-[#00ff87]/12 text-[#00ff87]" : "text-gray-400 hover:bg-white/5 hover:text-white"}`}
            >
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="rounded-2xl border border-white/10 bg-[#0a0a13] p-6">
          {tab === "analytics" && <AnalyticsManager />}
          {tab === "pages" && <PagesManager />}
          {tab === "blog" && <BlogManager />}
          {tab === "settings" && <SettingsManager />}
        </div>
      </div>
    </div>
  );
};

export default Admin;
